import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Image as ImageIcon, Check, X, Clock } from "lucide-react";
import { PLATFORMS, STATUSES } from "./_shared";
import { DraftBadge } from "@/components/DraftBadge";
// Relative, not "@/...": this infra is imported for real under Jest (no "@" alias there).
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";

function PlatformPicker({ editing, setEditing, t }) {
  return (
    <div>
      <Label>{t("social.platforms")}</Label>
      <div className="flex flex-wrap gap-2 mt-2">
        {PLATFORMS.map((p) => (
          <label key={p.id} className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-eh-border-soft cursor-pointer hover:bg-eh-cream">
            <input
              type="checkbox"
              checked={editing.platforms.includes(p.id)}
              onChange={(e) => {
                const newP = e.target.checked
                  ? [...editing.platforms, p.id]
                  : editing.platforms.filter((x) => x !== p.id);
                setEditing({ ...editing, platforms: newP });
              }}
              data-testid={`platform-${p.id}`}
            />
            {p.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function AiTextBox({ genTopic, setGenTopic, generating, onGenerate, aiDraft, onApproveDraft, onDiscardDraft, t }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
      <div className="text-xs text-amber-900 uppercase tracking-wider mb-2 flex items-center gap-1">
        <Sparkles className="w-3.5 h-3.5" /> {t("social.genWithAi")}
      </div>
      <Input
        value={genTopic}
        onChange={(e) => setGenTopic(e.target.value)}
        placeholder={t("social.topicPh")}
        data-testid="gen-topic"
        className="mb-2"
      />
      <Button size="sm" onClick={onGenerate} disabled={generating} data-testid="gen-text-btn">
        {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
        {t("social.genText")}
      </Button>

      {aiDraft && (
        <div className="mt-3 bg-white border border-amber-300 rounded-lg p-3" data-testid="ai-draft-preview">
          <div className="flex items-center justify-between mb-2">
            <DraftBadge />
            <span className="text-[10px] text-amber-800">{t("social.noTextWithoutValidation")}</span>
          </div>
          <p className="text-sm text-eh-ink whitespace-pre-wrap mb-1">{aiDraft.text}</p>
          {Array.isArray(aiDraft.hashtags) && aiDraft.hashtags.length > 0 && (
            <p className="text-xs text-eh-muted mb-3">{aiDraft.hashtags.map((h) => `#${h}`).join(" ")}</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={onApproveDraft} data-testid="ai-approve-btn" className="bg-eh-ink text-eh-bg">
              <Check className="w-3.5 h-3.5 mr-1" /> {t("social.approveUse")}
            </Button>
            <Button size="sm" variant="outline" onClick={onDiscardDraft} data-testid="ai-discard-btn">
              <X className="w-3.5 h-3.5 mr-1" /> {t("social.reject")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AiImageBox({ editing, genImagePrompt, setGenImagePrompt, genImage, onGenerate, t }) {
  return (
    <div className="bg-eh-cream/30 border border-eh-border-soft rounded-xl p-4">
      <div className="text-xs text-eh-muted uppercase tracking-wider mb-2 flex items-center gap-1">
        <ImageIcon className="w-3.5 h-3.5" /> {t("social.imageOptional")}
      </div>
      <Input
        value={genImagePrompt}
        onChange={(e) => setGenImagePrompt(e.target.value)}
        placeholder={t("social.imagePh")}
        data-testid="gen-image-prompt"
        className="mb-2"
      />
      <Button size="sm" onClick={onGenerate} disabled={genImage} data-testid="gen-image-btn">
        {genImage ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ImageIcon className="w-3.5 h-3.5 mr-1" />}
        {t("social.genImage")}
      </Button>
      {editing.image_url && <img src={editing.image_url} alt="" className="mt-3 rounded-lg max-h-48 object-cover" />}
    </div>
  );
}

/**
 * Why a post cannot be scheduled for later, as a small badge beside the schedule controls.
 * `scheduling` is the `social.publishing` state from the entitlements map: "coming_soon",
 * "not_in_plan", or null while the map loads (nothing is claimed then).
 */
function SchedulingBadge({ scheduling, t }) {
  if (scheduling !== "coming_soon" && scheduling !== "not_in_plan") return null;
  return (
    <span
      data-testid="post-scheduling-badge"
      className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-eh-muted bg-eh-cream border border-eh-border-soft rounded-full px-2 py-0.5"
    >
      <Clock className="w-3 h-3" />
      {scheduling === "coming_soon" ? t("ent.badge.comingSoon") : t("ent.badge.notInPlan")}
    </span>
  );
}

/**
 * `aiAvailable` — the AI assistant capability is usable: without it the two AI boxes are
 * not rendered at all (a button that can only answer "not built" is not an offer).
 * `scheduling` — the `social.publishing` state ("granted" | "coming_soon" |
 * "not_in_plan" | null). Only "granted" lets a post be scheduled for later; otherwise the
 * "scheduled" option stays visible but disabled, with the reason beside it, and the date
 * input is disabled with it. A post that ALREADY carries "scheduled" keeps showing it —
 * the option is disabled, not removed, so the select never misstates the saved value.
 */
export default function PostEditorModal({
  editing, setEditing, onSave, onClose,
  genTopic, setGenTopic, generating, onGenerateText,
  aiDraft, onApproveDraft, onDiscardDraft,
  genImagePrompt, setGenImagePrompt, genImage, onGenerateImage, t,
  fieldErrors = {}, clearFieldError = () => {}, tc = t,
  aiAvailable = false, scheduling = null,
}) {
  if (!editing) return null;
  const canSchedule = scheduling === "granted";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="social-editor">
        <h2 className="font-display text-2xl mb-6">{editing.id ? t("social.modalEditPost") : t("social.modalNewPost")}</h2>

        <div className="space-y-5">
          <PlatformPicker editing={editing} setEditing={setEditing} t={t} />

          {aiAvailable && (
            <AiTextBox
              genTopic={genTopic}
              setGenTopic={setGenTopic}
              generating={generating}
              onGenerate={onGenerateText}
              aiDraft={aiDraft}
              onApproveDraft={onApproveDraft}
              onDiscardDraft={onDiscardDraft}
              t={t}
            />
          )}

          <div>
            <Label>{t("social.content")}</Label>
            <textarea
              value={editing.content}
              onChange={(e) => { setEditing({ ...editing, content: e.target.value }); clearFieldError("content"); }}
              data-testid="post-content"
              rows={5}
              className={`w-full border rounded-md p-3 text-sm resize-none ${fieldErrors.content ? invalidInputClass : ""}`}
            />
            <FieldError message={fieldErrors.content ? tc(fieldErrors.content) : ""} testId="post-content-error" />
          </div>

          {aiAvailable && (
            <AiImageBox
              editing={editing}
              genImagePrompt={genImagePrompt}
              setGenImagePrompt={setGenImagePrompt}
              genImage={genImage}
              onGenerate={onGenerateImage}
              t={t}
            />
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("social.status")}</Label>
              <select
                value={editing.status}
                onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                className="w-full border rounded-md px-3 h-10 text-sm"
                data-testid="post-status"
              >
                {STATUSES.map((s) => (
                  <option
                    key={s}
                    value={s}
                    disabled={s === "scheduled" && !canSchedule}
                    data-testid={`post-status-option-${s}`}
                  >
                    {t(`social.statusOpt.${s}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>
                {t("social.scheduledDate")}
                <SchedulingBadge scheduling={scheduling} t={t} />
              </Label>
              <Input
                disabled={!canSchedule}
                type="datetime-local"
                value={editing.scheduled_at ? editing.scheduled_at.slice(0, 16) : ""}
                onChange={(e) => {
                  setEditing({ ...editing, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : "" });
                  clearFieldError("scheduled_at");
                }}
                data-testid="post-scheduled"
                className={fieldErrors.scheduled_at ? invalidInputClass : ""}
              />
              {/* `social.scheduledAtRequired` is an admin-namespace key (feature-specific
                  wording), unlike `content`'s common-namespace requiredField() messages
                  above — resolved through `t`, not `tc`. */}
              <FieldError message={fieldErrors.scheduled_at ? t(fieldErrors.scheduled_at) : ""} testId="post-scheduled-error" />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <Button onClick={onSave} data-testid="post-save" className="bg-eh-ink text-eh-bg flex-1">{t("social.save")}</Button>
          <Button onClick={onClose} variant="outline">{t("social.cancel")}</Button>
        </div>
      </div>
    </div>
  );
}
